import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useCells } from "../hooks/useCells";
import { useOffsetSortPagination } from "../hooks/useOffsetSortPagination";
import ServerSideTable from "../components/ServerSideTable";
import FilterChips from "../components/FilterChips";
import Button from "../components/Button";
import type { Cell } from "../lib/types";
import Card from "../components/Card";
import { useRole } from "../hooks/useRole";
import { exportApi } from "../lib/api";
import { Download } from "lucide-react";
import Skeleton from "../components/Skeleton";
import {
  applyListTableToSearchParams,
  initialSortingFromListUrl,
  readListTableSearch,
  readStatusChipParam,
  searchParamsEffectivelyEqual,
  serverPaginationInitFromListUrl,
} from "../lib/listUrlParams";

function formatDate(dateString?: string): string {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

const statusFilters = [
  { id: "all", label: "All", value: "" },
  { id: "in-stock", label: "In Stock", value: "In Stock" },
  { id: "reserved", label: "Reserved", value: "Reserved" },
  { id: "used", label: "Used", value: "Used" },
  { id: "scrapped", label: "Scrapped", value: "Scrapped" },
];

export default function Cells() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEditor } = useRole();
  const urlBootRef = useRef<{
    statusFilter: string[];
    table: ReturnType<typeof readListTableSearch>;
  } | null>(null);
  if (urlBootRef.current === null) {
    const sp = new URLSearchParams(window.location.search);
    urlBootRef.current = {
      statusFilter: readStatusChipParam(sp),
      table: readListTableSearch(sp),
    };
  }
  const urlBoot = urlBootRef.current;
  const [statusFilter, setStatusFilter] = useState(urlBoot.statusFilter);
  const {
    pagination,
    sorting,
    listQuery,
    onSortingChange,
    onPageSizeChange,
    clearSorting,
    syncCursorLastKey,
    canGoNext,
  } = useOffsetSortPagination(50, {
    initialSorting: initialSortingFromListUrl(
      urlBoot.table.sortId,
      urlBoot.table.orderDesc
    ),
    paginationInit: serverPaginationInitFromListUrl(
      urlBoot.table.page,
      urlBoot.table.sortId,
      urlBoot.table.cursors
    ),
  });

  const { data, isLoading, error } = useCells({
    status: statusFilter.length > 0 ? statusFilter[0] : undefined,
    limit: pagination.pageSize,
    ...listQuery,
  });

  useEffect(() => {
    syncCursorLastKey(data?.lastKey);
  }, [data?.lastKey, syncCursorLastKey]);

  useEffect(() => {
    const next = applyListTableToSearchParams(searchParams, {
      page: pagination.currentPage,
      sorting,
      lastKeys: pagination.lastKeys,
    });
    const status = statusFilter[0];
    if (status) next.set("status", status);
    else next.delete("status");
    if (!searchParamsEffectivelyEqual(next, searchParams)) {
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    pagination.currentPage,
    pagination.lastKeys,
    sorting,
    statusFilter,
  ]);

  const columns = [
    {
      header: "Cell ID",
      accessor: "cell_id" as keyof Cell,
      mobileLabel: "Cell ID",
      enableSorting: true,
    },
    {
      header: "Batch",
      accessor: "batch_id" as keyof Cell,
      mobileLabel: "Batch",
      enableSorting: true,
    },
    {
      header: "Box",
      accessor: "received_box" as keyof Cell,
      mobileLabel: "Box",
      enableSorting: true,
    },
    {
      header: "Shipment",
      accessor: "shipment_id" as keyof Cell,
      mobileLabel: "Shipment",
      enableSorting: true,
    },
    {
      header: "Received Date",
      accessor: "received_date" as keyof Cell,
      mobileLabel: "Received Date",
      enableSorting: true,
      cellRenderer: (cell: Cell) => formatDate(cell.received_date),
    },
    {
      header: "Capacity (kWh)",
      accessor: "cell_capacity_kwh" as keyof Cell,
      mobileLabel: "Capacity (kWh)",
      enableSorting: true,
      cellRenderer: (cell: Cell) =>
        cell.cell_capacity_kwh ? cell.cell_capacity_kwh.toFixed(4) : "-",
    },
    {
      header: "Type",
      accessor: "cell_type" as keyof Cell,
      mobileLabel: "Type",
      enableSorting: true,
      cellRenderer: (cell: Cell) => cell.cell_type || "-",
    },
    {
      header: "Terminal Type",
      accessor: "terminal_type" as keyof Cell,
      mobileLabel: "Terminal Type",
      enableSorting: true,
      cellRenderer: (cell: Cell) => cell.terminal_type || "-",
    },
    {
      header: "Status",
      accessor: "status" as keyof Cell,
      mobileLabel: "Status",
      enableSorting: true,
      cellRenderer: (cell: Cell) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${cell.status === "In Stock"
            ? "bg-green-50 text-green-700 border border-green-200"
            : cell.status === "Used"
              ? "bg-blue-50 text-blue-700 border border-blue-200"
              : cell.status === "Reserved"
                ? "bg-amber-50 text-amber-700 border border-amber-200"
                : "bg-gray-50 text-gray-700 border border-gray-200"
            }`}
        >
          {cell.status || "Unknown"}
        </span>
      ),
    },
    {
      header: "Pack",
      accessor: "pack_id" as keyof Cell,
      mobileLabel: "Pack",
      enableSorting: true,
      cellRenderer: (cell: Cell) => {
        if (cell.pack_id) {
          return (
            <div className="max-w-[150px]">
              <Link
                to={`/packs/${cell.pack_id}`}
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium truncate block"
                onClick={(e) => e.stopPropagation()}
                title={cell.pack_id}
              >
                {cell.pack_id}
              </Link>
            </div>
          );
        }
        if (cell.pack_usage && cell.pack_usage.length > 0) {
          const packIds = cell.pack_usage
            .map((usage) => usage.pack_id)
            .join(", ");
          return (
            <span className="text-gray-600 text-sm" title={packIds}>
              {cell.pack_usage.length} pack
              {cell.pack_usage.length > 1 ? "s" : ""}
            </span>
          );
        }
        return <span className="text-gray-400">-</span>;
      },
    },
  ];

  const handlePageChange = (direction: "next" | "prev" | "first" | "last") => {
    if (direction === "next") {
      pagination.nextPage();
    } else if (direction === "prev") {
      pagination.previousPage();
    } else if (direction === "first") {
      pagination.reset();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900 leading-tight">
            Cell Inventory
          </h1>
          <p className="text-sm md:text-lg text-gray-500 font-medium mt-1">
            Manage and track battery cell inventory
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                await exportApi.download('cells');
              } catch (error) {
                alert('Failed to export cells');
              }
            }}
            className="flex items-center gap-2 md:flex-none"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Export
          </Button>
          {isEditor && (
            <Button
              variant="primary"
              onClick={() => navigate("/cells/new")}
              className="flex-1 md:flex-none shadow-md"
            >
              + Add New Cell
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <FilterChips
          filters={statusFilters}
          activeFilters={statusFilter}
          onToggle={(value) => {
            if (value === "") {
              setStatusFilter([]);
            } else {
              setStatusFilter([value]);
              pagination.reset();
            }
            clearSorting();
          }}
          onClear={() => {
            setStatusFilter([]);
            pagination.reset();
            clearSorting();
          }}
        />
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Card className="p-0 overflow-hidden">
            <div className="space-y-4 p-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      ) : error ? (
        <Card>
          <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h4 className="text-sm font-semibold text-red-900 mb-1">
                  Error Loading Data
                </h4>
                <p className="text-sm text-red-700 mb-2">
                  {error instanceof Error
                    ? error.message
                    : "Failed to load cells. Please check if the backend server is running."}
                </p>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <ServerSideTable
          columns={columns}
          data={data?.items || []}
          onRowClick={(cell) => navigate(`/cells/${cell.cell_id}`)}
          emptyMessage="No cells found"
          total={data?.total}
          currentPage={pagination.currentPage}
          pageSize={pagination.pageSize}
          canGoNext={canGoNext(data?.total ?? 0, data?.lastKey)}
          canGoPrevious={pagination.canGoPrevious}
          onPageChange={handlePageChange}
          onPageSizeChange={onPageSizeChange}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      )}
    </div>
  );
}
